import {
  type PaletteMode as MuiPaletteMode,
  createTheme as createMuiTheme,
} from '@mui/material';
import { createComponents } from './aspects/components';
import { createMeasurements } from './aspects/measurements';
import { createMixins } from './aspects/mixins';
import { type ThemeCustomPalette, createPalette } from './aspects/palette';
import { createShadows } from './aspects/shadows';
import { createTypography } from './aspects/typography';
import { createUtils } from './aspects/utils';
import { convertToMuiThemeMode } from './internals/utils';
import type { Theme, ThemeMode } from './types';

const themeMap = {
  dark: createThemeBase('dark'),
  light: createThemeBase('light'),
  twilight: createThemeBase('twilight'),
} as const;

/**
 * The themes available in the application.
 */
export const themes = [
  {
    name: 'dark',
    displayName: 'Dark',
    example: getExample('dark'),
  },
  {
    name: 'light',
    displayName: 'Light',
    example: getExample('light'),
  },
  {
    name: 'twilight',
    displayName: 'Twilight',
    example: getExample('twilight'),
  },
] as const;

/**
 * Create a theme.
 * @param mode The theme mode.
 * @returns The theme.
 */
export function createTheme(mode: ThemeMode): Theme {
  return themeMap[mode];
}

/*
|----------------------------------
| Internal
|----------------------------------
*/

function patchPalette(
  palette: ThemeCustomPalette
): ThemeCustomPalette<MuiPaletteMode> {
  return {
    ...palette,
    mode: convertToMuiThemeMode(palette.mode),
  };
}

function getExample(mode: ThemeMode): {
  background: string;
  text: string;
  border: string;
  primary: string;
} {
  const t = themeMap[mode];
  return {
    background: t.palette.background.default,
    text: t.palette.text.primary,
    border: t.palette.divider,
    primary: t.palette.primary.main,
  };
}

function createThemeBase(mode: ThemeMode): Theme {
  const palette = createPalette(mode);
  const shadows = createShadows(mode);
  const baseTheme = createMuiTheme({
    palette: patchPalette(palette),
    shadows,
  }) as unknown as Theme;
  const typography = createTypography(baseTheme);
  const components = createComponents(baseTheme);
  const mixins = createMixins(baseTheme);
  const measurements = createMeasurements(baseTheme);
  const utils = createUtils(baseTheme);

  const finalTheme = createMuiTheme({
    direction: 'ltr',
    palette: patchPalette(palette),
    typography,
    components,
    mixins,
    shadows,
  });

  return {
    ...finalTheme,
    // We need to override the mode here to ensure that the mode always includes all the themes
    palette: {
      ...finalTheme.palette,
      mode,
    },
    measurements,
    ...utils,
  };
}
